import React, { useRef, useState } from 'react';
import { DataGridTypes } from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react';
import { formatMessage } from 'devextreme/localization';

import TransformationPopup from './TransformationPopup';
import ComplexTransformationPopup from './ComplexTransformationPopup';
import GenerateAITransformationResult from '../../classes/api/result/pathsynchromodule/GenerateAITransformationResult';
import TransformationTypeDto from '../../classes/dtos/pathcommonmodule/TransformationTypeDto';
import TransformationCapabilitiesDto from '../../classes/dtos/transformationmodule/TransformationCapabilitiesDto';
import TransformationOptionsDto from '../../classes/dtos/transformationmodule/TransformationOptionsDto';

interface TransformationValue {
    type: number | null;
    parameters: string;
}

interface UseTransformationColumnOptions {
    // Field names on the row data this column edits (e.g. 'TransformationType'/'TransformationParameters'
    // or 'AggregationType'/'AggregationParameters'), and the field used as the row key (varies per module).
    rowKeyField: string;
    typeField: string;
    parametersField: string;

    title: string;
    transformationsCapabilities: TransformationCapabilitiesDto[];

    // Module-specific: which transformation types are selectable for a given row.
    calculateAvailableTypes: (rowData: any) => TransformationTypeDto[];
    getOptionsDetails: (type: number | null) => TransformationOptionsDto | null;
    getConstantFieldType?: (type: number | null) => number | null;

    // Synchro historically shows the plain capability label instead of "Label -> TargetType".
    labelFormatter?: (found: TransformationCapabilitiesDto) => string;

    // Whether the button should be disabled when the row has no real (non-"Direct") option to configure.
    disabledWhenNoOptions?: boolean;

    // Whether a transformation MUST be configured for this row: "Direct" (LogicId 0) is not an allowed
    // choice, so the row can't be left untransformed. When true, the button turns red while the entry
    // is incomplete and green once it's fully filled in. Optional transformations keep the neutral look.
    isTransformationRequired?: (rowData: any) => boolean;

    // Optional: given the full grid cell, returns the row data the button/popup logic should read,
    // with pending edits from *other* columns folded in (e.g. Synchro's source-field pick, read live
    // from `cellData.component.cellValue()`). Without it the button can't see a freshly picked source
    // field and stays disabled. The default just unwraps `cellData.data`.
    resolveRowData?: (cellData: any) => any;

    // Optional: when it returns false for a row, the button is hidden entirely (a per-row "this row
    // can't carry a transformation" rule). The exposed-field grids instead hide the button on
    // unselected rows purely in CSS (`.dx-data-row:not(.dx-selection) .hide-btn-when-unselected`),
    // so they no longer pass this.
    isRowActive?: (rowData: any) => boolean;

    // Optional: AI-generated "complex" transformation. When a row's only selectable type is `logicId`
    // (or that type is already applied), the dedicated prompt popup opens instead of the standard one.
    // `onGenerate` receives the row data (live values folded in) and the user prompt, and resolves to
    // the generated settings and the labels of the transformations they use (or rejects with a displayable message).
    complexTransformation?: {
        logicId: number;
        // Readable name for a source field id shown in the step graph.
        getFieldLabel?: (id: string) => string;
        onGenerate: (rowData: any, prompt: string) => Promise<GenerateAITransformationResult>;
    };
}

interface UseTransformationColumnResult {
    ButtonCell: (cellData: DataGridTypes.ColumnCellTemplateData) => JSX.Element;
    PopupElement: JSX.Element;
    calculateDisplayValue: (rowData: any) => string;

    // The transformation type as currently applied, PENDING BATCH EDITS INCLUDED, when called with a
    // grid cell template arg (`{ component, rowIndex }`) — it reads `component.cellValue()`, which
    // reflects unsaved changes in batch mode. Falls back to the persisted `rowData[typeField]` when
    // called with a plain row (the sort / filter paths have no grid handle). Lets a derived column
    // (e.g. the computed TARGET field type) refresh on repaint instead of staying stale until save.
    getEffectiveType: (cellDataOrRow: any) => number | null;
}

/**
 * Shared logic behind the "TRANSFORMATION"/"AGGREGATION" button + popup used across the
 * Synchro, Reporting, MetaApi, DataTable and AI mapping grids.
 *
 * The grid's own batch-edit state is the single source of truth: in batch mode
 * `component.cellValue(rowIndex, field)` already returns the value with unsaved changes applied,
 * so there is no React mirror of the pending edits to keep in sync. The button column is a
 * calculated `cellRender` whose value (the type label) doesn't change on a parameter-only edit,
 * so DevExtreme won't repaint it by itself — every write is therefore followed by an explicit
 * `repaintRows()`.
 */
function useTransformationColumn(options: UseTransformationColumnOptions): UseTransformationColumnResult {
    const {
        rowKeyField, typeField, parametersField, title,
        transformationsCapabilities, calculateAvailableTypes,
        getOptionsDetails, getConstantFieldType,
        labelFormatter, disabledWhenNoOptions = true,
        isTransformationRequired, resolveRowData, isRowActive, complexTransformation
    } = options;

    const resolve = resolveRowData ?? ((cellData: any) => cellData?.data ?? cellData);

    // Read the predicate through a ref: devextreme-react can run an older ButtonCell closure after a
    // repaint, so `isRowActive` must always be re-read from the latest render.
    const isRowActiveRef = useRef(isRowActive);
    isRowActiveRef.current = isRowActive;

    const [popup, setPopup] = useState<{ cellData: DataGridTypes.ColumnCellTemplateData } | null>(null);

    const labelForType = (type: number | null | undefined): string => {
        if (type === null || type === undefined || type === 0) {
            return "";
        }
        const found = transformationsCapabilities.find((x) => x.LogicId === type) ?? null;
        if (found === null) {
            return "";
        }
        return labelFormatter ? labelFormatter(found) : found.GetDescription();
    };

    // Current type/parameters for a row. Given a grid cell arg it reads the live batch value
    // (`component.cellValue`), which includes unsaved edits; given a plain row it returns the
    // persisted fields (sort / filter paths, which have no grid handle).
    const getEffective = (arg: any): TransformationValue => {
        const component = arg?.component;
        const rowIndex = arg?.rowIndex ?? arg?.row?.rowIndex;
        const rowData = arg?.data ?? arg;
        const hasGridHandle =
            component && typeof component.cellValue === 'function' &&
            rowIndex !== undefined && rowIndex !== null && rowIndex >= 0 &&
            // During virtual scrolling DevExtreme can call cellRender for a recycled row before its
            // internal cellValue cache has caught up with the row now sitting at that index; cellValue
            // would then briefly return another row's (or no) value, flashing the button red until the
            // next repaint resyncs the cache. Guard against it by checking the row key still matches.
            component.cellValue(rowIndex, rowKeyField) === rowData?.[rowKeyField];

        if (hasGridHandle) {
            return {
                type: component.cellValue(rowIndex, typeField) ?? null,
                parameters: component.cellValue(rowIndex, parametersField) ?? "",
            };
        }

        return { type: rowData?.[typeField] ?? null, parameters: rowData?.[parametersField] ?? "" };
    };

    const calculateDisplayValue = (rowData: any): string => labelForType(rowData[typeField]);

    const getEffectiveType = (cellDataOrRow: any): number | null => getEffective(cellDataOrRow).type;

    // "Complete" = a type is picked (Id != 0) AND, when that type needs a parameter (a constant
    // value or a picked option Id), that parameter is filled in.
    const isComplete = (effective: TransformationValue): boolean => {
        if (effective.type === null || effective.type === undefined || effective.type === 0) {
            return false;
        }

        // Constant transformation: needs a non-empty Value.
        const constantFieldType = getConstantFieldType ? getConstantFieldType(effective.type) : null;
        if (constantFieldType !== null) {
            try {
                const value = JSON.parse(effective.parameters || "").Value;
                return value !== undefined && value !== null && String(value).trim().length > 0;
            } catch {
                return false;
            }
        }

        // Option transformation: needs a picked option Id.
        const details = getOptionsDetails(effective.type);
        if (details && details.Options && details.Options.length > 0) {
            try {
                return !!JSON.parse(effective.parameters || "").Id;
            } catch {
                return false;
            }
        }

        // No parameter needed: picking the type is enough.
        return true;
    };

    const ButtonCell = (cellData: DataGridTypes.ColumnCellTemplateData) => {
        const rowData = resolve(cellData);

        // Row isn't selected/exposed: nothing to transform on it, hide the button.
        if (isRowActiveRef.current && !isRowActiveRef.current(rowData)) {
            return <></>;
        }

        const effective = getEffective(cellData);
        const label = labelForType(effective.type);
        const isConfigured = !!label;
        const availableTypes = calculateAvailableTypes(rowData);
        const canConfigure = !disabledWhenNoOptions || availableTypes.length > 0;

        // Nothing to configure on this row: hide the button entirely rather than showing a dead,
        // disabled one.
        if (!canConfigure) {
            return <></>;
        }

        // Colour rule:
        //  - a transformation type is picked (Id != 0): green if the entry is complete (any required
        //    parameter filled in), red if a required parameter is still missing;
        //  - no type picked but one is mandatory ("Direct" not allowed): red;
        //  - otherwise: neutral configured/unconfigured styling.
        const hasType = effective.type !== null && effective.type !== undefined && effective.type !== 0;
        const required = (isTransformationRequired?.(rowData) ?? false) && availableTypes.length > 0;
        let stylingMode: "contained" | "outlined" = isConfigured ? "contained" : "outlined";
        let type: "default" | "normal" | "danger" | "success" = isConfigured ? "default" : "normal";
        if (hasType || required) {
            stylingMode = "contained";
            type = isComplete(effective) ? "success" : "danger";
        }

        return (
            <Button
                text={label || formatMessage("TransformationPopup_Configure")}
                icon="edit"
                stylingMode={stylingMode}
                type={type}
                width="100%"
                elementAttr={{ class: "transformation-button-cell" }}
                onClick={(e) => {
                    e.event?.stopPropagation();
                    setPopup({ cellData });
                }}
            />
        );
    };

    const applyToRow = (newType: number | null, newParameters: string) => {
        if (popup) {
            const dataGrid = popup.cellData.component;
            const rowIndex = popup.cellData.row.rowIndex;
            const newParams = newType === null ? "" : newParameters;
            dataGrid.cellValue(rowIndex, typeField, newType);
            dataGrid.cellValue(rowIndex, parametersField, newParams);
            // cellValue() has updated the batch state synchronously; the button's own
            // calculated value (the type label) is unchanged on a parameter-only edit,
            // so DevExtreme won't repaint the cell on its own. Force it — ButtonCell
            // then re-reads the fresh value.
            dataGrid.repaintRows([rowIndex]);
        }
        setPopup(null);
    };

    const popupRowData = popup ? resolve(popup.cellData) : null;
    const popupTypes = popupRowData ? calculateAvailableTypes(popupRowData) : [];
    const isComplexPopup = !!complexTransformation && popup !== null && (
        getEffective(popup.cellData).type === complexTransformation.logicId ||
        (popupTypes.length === 1 && popupTypes[0].Id === complexTransformation.logicId));

    const PopupElement = isComplexPopup && complexTransformation ? (
        <ComplexTransformationPopup
            visible={true}
            title={formatMessage("ComplexTransformationPopup_Title")}
            currentParameters={getEffective(popup!.cellData).type === complexTransformation.logicId ? getEffective(popup!.cellData).parameters : ""}
            transformationsCapabilities={transformationsCapabilities}
            getFieldLabel={complexTransformation.getFieldLabel}
            onGenerate={(prompt) => complexTransformation.onGenerate(popupRowData, prompt)}
            onApply={(parameters) => applyToRow(complexTransformation.logicId, parameters)}
            onCancel={() => setPopup(null)}
        />
    ) : (
        <TransformationPopup
            visible={popup !== null}
            title={title}
            typeOptions={popupTypes}
            currentType={popup ? getEffective(popup.cellData).type : null}
            currentParameters={popup ? getEffective(popup.cellData).parameters : ""}
            getOptionsDetails={getOptionsDetails}
            getConstantFieldType={getConstantFieldType}
            onApply={applyToRow}
            onCancel={() => setPopup(null)}
        />
    );

    return { ButtonCell, PopupElement, calculateDisplayValue, getEffectiveType };
}

export default useTransformationColumn;
